import { Handle, Position } from '@xyflow/react';

interface APINodeData extends Record<string, unknown> {
  label: string;
  type: 'api';
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  url: string;
  headers?: Record<string, string>;
  body?: string;
  timeout?: number;
  retryCount?: number;
  outputVariable?: string;
}

interface APINodeProps {
  data: APINodeData;
  selected?: boolean;
}

const methodColors: Record<string, { bg: string; border: string }> = {
  GET: { bg: '#22c55e', border: '#16a34a' },
  POST: { bg: '#3b82f6', border: '#2563eb' },
  PUT: { bg: '#f59e0b', border: '#d97706' },
  DELETE: { bg: '#ef4444', border: '#dc2626' },
  PATCH: { bg: '#8b5cf6', border: '#7c3aed' },
};

export default function APINode({ data, selected }: APINodeProps) {
  const method = data.method || 'GET';
  const colors = methodColors[method] || methodColors.GET;

  return (
    <div
      style={{
        minWidth: '200px',
        maxWidth: '280px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        border: selected ? '3px solid #1d4ed8' : '2px solid #3b82f6',
        borderRadius: '12px',
        padding: '12px 16px',
        color: 'white',
        boxShadow: selected
          ? '0 0 0 4px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)'
          : '0 4px 12px rgba(0, 0, 0, 0.15)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: '12px',
          height: '12px',
          background: '#3b82f6',
          border: '2px solid white',
        }}
      />
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontSize: '16px' }}>🔌</span>
        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{data.label}</span>
      </div>
      
      {/* Method Badge */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(255,255,255,0.15)',
          padding: '4px 8px',
          borderRadius: '4px',
          marginBottom: '8px',
        }}
      >
        <span
          style={{
            fontSize: '10px',
            fontWeight: 'bold',
            padding: '2px 6px',
            borderRadius: '3px',
            background: colors.bg,
            color: 'white',
          }}
        >
          {method}
        </span>
      </div>
      
      {/* URL */}
      {data.url && (
        <div
          style={{
            fontSize: '11px',
            opacity: 0.9,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            background: 'rgba(255,255,255,0.1)',
            padding: '6px 8px',
            borderRadius: '6px',
            fontFamily: 'monospace',
          }}
        >
          {data.url}
        </div>
      )}
      
      {/* Timeout info */}
      {data.timeout && (
        <div
          style={{
            fontSize: '10px',
            opacity: 0.8,
            marginTop: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span>⏱️</span>
          <span>Timeout: {data.timeout}ms</span>
          {data.retryCount && data.retryCount > 0 && (
            <span style={{ marginLeft: '8px' }}>🔄 Retry: {data.retryCount}</span>
          )}
        </div>
      )}
      
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: '12px',
          height: '12px',
          background: '#3b82f6',
          border: '2px solid white',
        }}
      />
    </div>
  );
}
